import { Module } from "@ak/game-assets"
import cls from "classnames"
import React, { FC, useMemo, useState } from "react"

// import { RuleButton } from "@/Topics/Recall/components/Modals/Rules"
import { CommonTransition } from "@/components/CommonTransition"
import OutlineButton from "@/components/OutlineButton"
import OutlinedText from "@/components/OutlinedText"
import { useElemRef } from "@/hooks/useElemRef"
import { i18n, RecallTextPath } from "@/i18n"
import { RLRewardMap, RLSrcs, VRRewardMap } from "@/Topics/Recall/const/reward"
import { Client } from "@/utils/gameAssets"

import styles from "./index.module.scss"

export const Pool = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const list = useMemo(() => {
        const vrList = Object.values(VRRewardMap)
            .filter((q) => !q?.isReveal)
            .map((reward) => {
                return {
                    ...reward,
                    src: Client.get(Module.ITEM, reward.key as string),
                    title: i18n.getRecall(reward.name as RecallTextPath) || "-",
                }
            })
        const rlList = Object.values(RLRewardMap).map((q) => {
            return {
                ...q,
                src: RLSrcs[q.key as keyof typeof RLSrcs],
                title: i18n.getRecall(q.name as RecallTextPath) || "-",
            }
        })
        return [...vrList, ...rlList].sort((a, b) => a.order - b.order)
    }, [])
    const pageSize = 4
    const totalPages = Math.ceil(list.length / pageSize)

    return (
        <div className={styles.modal}>
            {/* <RuleButton /> */}
            <div className={styles.title}>
                <OutlinedText
                    className={styles.outlinedText}
                    text={i18n.getRecall("pool.title")}
                    strokeColor="#8F7254"
                    fillColor="#fffbf7"
                    strokeWidth={6}
                    fontSize={40}
                />
            </div>
            <div className={styles.pool}>
                <ListViewer
                    list={list}
                    pageSize={pageSize}
                    currentPage={currentPage}
                />
            </div>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={() => {
                    if (currentPage <= 1) {
                        return
                    }
                    setCurrentPage(currentPage - 1)
                }}
                onNext={() => {
                    if (currentPage >= totalPages) {
                        return
                    }
                    setCurrentPage(currentPage + 1)
                }}
            />
        </div>
    )
}

const ListViewer: FC<{
    list: any[]
    pageSize: number
    currentPage: number
}> = ({ list, pageSize, currentPage }) => {
    const pagedList = useMemo(() => {
        return list.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    }, [list, currentPage, pageSize])

    const listRef = useElemRef()

    return (
        <div className={styles.viewer}>
            <CommonTransition nodeRef={listRef} state={String(currentPage)}>
                <div ref={listRef} className={styles.list}>
                    {pagedList.map((item) => (
                        <ListItem
                            key={item.key}
                            title={item.title}
                            src={item.src}
                            amount={item?.amount}
                            total={item?.total}
                        />
                    ))}
                </div>
            </CommonTransition>
        </div>
    )
}

const Pagination: FC<{
    currentPage: number
    totalPages: number
    onPrev: () => void
    onNext: () => void
}> = ({ currentPage, totalPages, onPrev, onNext }) => {
    return (
        <div className={styles.pagination}>
            <OutlineButton
                className={cls(styles.btn, styles.prev)}
                text={i18n.getRecall("pool.button_prev")}
                onClick={onPrev}
                disabled={currentPage <= 1}
                fontSize={30}
                strokeWidth={6}
                strokeColor="#1F5A5F"
            />
            <div className={styles.page}>
                <OutlinedText
                    className={styles.outlinedText}
                    text={`0${currentPage} / 0${totalPages}`}
                    strokeColor="#fbe8d3"
                    strokeWidth={2}
                    fillColor="#8f7254"
                    fontSize={24}
                    fontFamily="SourceHanSansJP-VF"
                />
            </div>
            <OutlineButton
                className={styles.btn}
                text={i18n.getRecall("pool.button_next")}
                onClick={onNext}
                disabled={currentPage >= totalPages}
                fontSize={30}
                strokeWidth={6}
                strokeColor="#872D19"
                fillGradient={{ from: "#FFFAF6", to: "#FCF1D2" }}
            />
        </div>
    )
}

const ListItem: FC<{
    title: string
    src: string
    amount?: number
    total?: number
}> = ({ title, src, amount }) => {
    // const { t } = useTranslation()
    const t = i18n.getRecall
    return (
        <div className={styles.item}>
            <div className={styles.left}>
                <img src={src} alt={title} />
            </div>
            <div className={styles.right}>
                <div
                    className={cls(styles.itemTitle, {
                        [styles.whole]: true,
                    })}
                >
                    <div className={styles.inner}>{title}</div>
                </div>
                <div className={styles.divider} />
                <div className={styles.amount}>
                    <div className={styles.prefix}>
                        {t("pool.amount_prefix")}
                    </div>
                    <div className={styles.count}>{amount}</div>
                </div>
            </div>
        </div>
    )
}
